import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DashboardHeader from "@/components/layout/DashboardHeader";
import ImageCarousel from "@/components/issue/ImageCarousel";
import LocationMap from "@/components/issue/LocationMap";
import CommunicationPanel from "@/components/issue/CommunicationPanel";
import IssueDetailsComponent from "@/components/issue/IssueDetails";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

// Mock issue data for the prototype
const issueData = {
  "CR-83451": {
    id: "CR-83451",
    category: "pothole",
    location: { lat: 40.7128, lng: -74.006 },
    dateReported: "2024-10-26 09:15 AM",
    status: "open" as const,
    images: [
      "https://storage.googleapis.com/fenado-ai-farm-public/generated/54e9b174-883f-4af0-b755-2b6679671ee5.png",
    ],
  },
  "CR-83452": {
    id: "CR-83452",
    category: "garbage",
    location: { lat: 34.0522, lng: -118.2437 },
    dateReported: "2024-10-26 08:45 AM",
    status: "open" as const,
    images: [
      "https://storage.googleapis.com/fenado-ai-farm-public/generated/37f75e46-4840-4a64-8dc8-129216d4876b.png",
    ],
  },
  "CR-83449": {
    id: "CR-83449",
    category: "streetlight",
    location: { lat: 41.8781, lng: -87.6298 },
    dateReported: "2024-10-25 11:20 PM",
    status: "closed" as const,
    images: [
      "https://storage.googleapis.com/fenado-ai-farm-public/generated/0123fd02-ac20-4bc2-98f4-a26ba56a1424.png",
    ],
  },
  "CR-83448": {
    id: "CR-83448",
    category: "pothole",
    location: { lat: 40.7145, lng: -74.0081 },
    dateReported: "2024-10-25 04:30 PM",
    status: "in-progress" as const,
    images: [
      "https://storage.googleapis.com/fenado-ai-farm-public/generated/54e9b174-883f-4af0-b755-2b6679671ee5.png",
    ],
  },
};

const IssueDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [issue, setIssue] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setIsLoading(true);

        const token = localStorage.getItem("authToken");
        if (!token) {
          navigate("/login");
          return;
        }

        if (!id) {
          throw new Error("No report ID provided");
        }

        const response = await fetch(
          `${import.meta.env.VITE_BE_URL}/api/v1/reports/${id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            navigate("/dashboard");
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: any = await response.json();
        setIssue(data.data);
      } catch (err) {
        console.error("Failed to fetch report:", err);
        toast.error(
          err instanceof Error ? err.message : "Failed to load report"
        );
        navigate("/dashboard");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [id, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <DashboardHeader />

        <main className="flex-grow container mx-auto px-4 py-6">
          <div className="mb-6">
            <Button variant="ghost" className="mb-4" disabled>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>

            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Skeleton className="h-64 w-full mb-4 rounded-lg" />
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>

            <div>
              <Skeleton className="h-64 w-full mb-4 rounded-lg" />
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!issue) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <DashboardHeader />

      <main className="flex-grow container mx-auto px-4 py-6">
        <div className="mb-6">
          <Button variant="ghost" className="mb-4" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </Button>

          <h1 className="text-2xl font-bold mb-2">
            Issue Details: #{issue.reportId}
          </h1>
          <p className="text-muted-foreground">
            View details and manage the reported issue
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Visual Content */}
          <div className="space-y-6">
            <ImageCarousel
              images={!!issue.imageUrl ? [issue.imageUrl] : []}
              title={`${issue.category} issue`}
            />
            <LocationMap lat={issue.lat} lng={issue.lng} />
          </div>

          {/* Right Column: Details & Communication */}
          <div className="space-y-6">
            <IssueDetailsComponent issue={issue} />
            <CommunicationPanel issueId={issue.reportId} issue={issue} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default IssueDetails;
